import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Descriptions, Space, Tag, Typography } from 'antd';

const { Text } = Typography;
const activeStatuses = new Set(['Queued', 'Syncing']);
const colors = {
  Queued: 'gold',
  Syncing: 'processing',
  Completed: 'success',
  'Completed with errors': 'warning',
  Failed: 'error'
};

const formatTime = value => value ? new Date(value).toLocaleString() : '—';

const SchedoxSyncStatus = ({ loadStatus, startSync, onStatus }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const mounted = useRef(true);

  const refresh = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await loadStatus();
      if (mounted.current) {
        const status = response?.data || response;
        setData(status);
        onStatus?.(status);
        setRequestError(null);
      }
    } catch (error) {
      if (mounted.current) setRequestError('Sync status is temporarily unavailable.');
    } finally {
      if (mounted.current && !quiet) setLoading(false);
    }
  }, [loadStatus, onStatus]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  useEffect(() => {
    if (!activeStatuses.has(data?.run?.status)) return undefined;
    const timer = window.setInterval(() => refresh(true), 3000);
    return () => window.clearInterval(timer);
  }, [data?.run?.status, refresh]);

  const handleSync = async () => {
    setLoading(true);
    try {
      await startSync();
      await refresh(true);
    } catch (error) {
      if (mounted.current) {
        setRequestError(error?.response?.data?.message || 'The sync could not be queued. Please try again.');
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const sources = data?.sources || {};
  const run = data?.run;
  const isActive = activeStatuses.has(run?.status);

  return (
    <div style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <Space wrap style={{ marginBottom: 10 }}>
        <Text strong>Schedox sync</Text>
        <Tag>User {sources.user_toggle ? 'on' : 'off'}</Tag>
        {Number.isInteger(sources.total_property_count) ? (
          <Tag>Properties {sources.enabled_property_count || 0}/{sources.total_property_count}</Tag>
        ) : sources.property_toggle != null && <Tag>Property {sources.property_toggle ? 'on' : 'off'}</Tag>}
        <Tag color={sources.effective ? 'green' : 'default'}>Effective {sources.effective ? 'on' : 'off'}</Tag>
        {run?.status && <Tag color={colors[run.status]}>{run.status}</Tag>}
      </Space>

      {run && (
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="Started">{formatTime(run.started_at)}</Descriptions.Item>
          <Descriptions.Item label="Finished">{formatTime(run.completed_at)}</Descriptions.Item>
          <Descriptions.Item label="Last success">{formatTime(run.last_successful_at)}</Descriptions.Item>
          <Descriptions.Item label="Result">
            {run.reviewed || 0} reviewed · {run.created || 0} created · {run.updated || 0} updated · {run.skipped || 0} skipped · {run.failed || 0} failed
          </Descriptions.Item>
        </Descriptions>
      )}
      {run?.error && <Alert style={{ marginBottom: 10 }} type={run.status === 'Failed' ? 'error' : 'warning'} message={run.error} showIcon />}
      {requestError && <Alert style={{ marginBottom: 10 }} type="warning" message={requestError} showIcon />}
      <Button size="small" loading={loading || isActive} disabled={!sources.effective || isActive} onClick={handleSync}>
        {isActive ? run.status : 'Sync now'}
      </Button>
    </div>
  );
};

export default React.memo(SchedoxSyncStatus);
